'use client';

/**
 * Comprehensive Conversion Tracking
 * - Fires GA4 events via gtag
 * - Fires Google Ads conversion tags (dedicated conversion IDs per action)
 * - Saves every event to Supabase via /api/track-conversion (server-side, reliable)
 * - Sends real-time email alerts for high-value conversions
 */

// ─── Google Ads Conversion IDs ────────────────────────────────────────────────
// Replace these with your actual Google Ads conversion IDs from:
// Google Ads → Tools → Conversions → New conversion action → Website
// Format: AW-XXXXXXXXX/LABEL
export const GOOGLE_ADS_CONVERSIONS = {
  phone_call:        'AW-XXXXXXXXX/PHONE_CALL_LABEL',
  whatsapp_click:    'AW-XXXXXXXXX/WHATSAPP_CLICK_LABEL',
  offerte_form:      'AW-XXXXXXXXX/OFFERTE_FORM_LABEL',
  contact_form:      'AW-XXXXXXXXX/CONTACT_FORM_LABEL',
  appointment_click: 'AW-XXXXXXXXX/APPOINTMENT_CLICK_LABEL',
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────

export type ConversionType =
  | 'phone_call' | 'whatsapp_click' | 'contact_form' | 'offerte_form'
  | 'homepage_contact_form' | 'appointment_click' | 'offerte_button_click';

export type HighValueConversionType = 'phone_call' | 'whatsapp_click' | 'offerte_form' | 'homepage_contact_form';

interface ConversionParams {
  location?: string;
  formId?: string;
  dienst?: string;
  vehicleCategory?: string;
  citySlug?: string;
  cityName?: string;
  naam?: string;
  telefoon?: string;
  email?: string;
  metadata?: Record<string, string | number | boolean>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getUtmParams(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_content: params.get('utm_content') || '',
    utm_term: params.get('utm_term') || '',
  };
}

function getSessionId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let sid = sessionStorage.getItem('rc_session_id');
    if (!sid) {
      sid = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      sessionStorage.setItem('rc_session_id', sid);
    }
    return sid;
  } catch {
    return '';
  }
}

function fireGtag(eventName: string, params: Record<string, string | number | boolean>) {
  if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
    (window as any).gtag('event', eventName, params);
  }
}

// ─── Google Ads conversion ping ───────────────────────────────────────────────

function fireGoogleAdsConversion(
  conversionId: string,
  value: number = 1,
  currency: string = 'EUR'
) {
  if (
    typeof window !== 'undefined' &&
    typeof (window as any).gtag === 'function'&& !conversionId.includes('XXXXXXXXX')
  ) {
    (window as any).gtag('event', 'conversion', {
      send_to: conversionId,
      value,
      currency,
    });
  }
}

// ─── High-value conversion alert ─────────────────────────────────────────────
// Email alerts for button clicks have been disabled.
// All events are stored in Supabase and visible in /admin/marketing dashboard.
// Only contact form lead emails are sent via /api/send-contact.

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function sendHighValueAlert(
  _type: HighValueConversionType,
  _params: ConversionParams,
  _pagePath: string,
  _utms: Record<string, string>
) {
  // Disabled — no email sent for button/click events
  return;
}

// ─── Supabase persistence via server API route ────────────────────────────────
// Using a server-side API route instead of direct browser Supabase client
// to ensure reliable persistence regardless of browser/cookie state.

async function saveConversionToSupabase(
  eventType: ConversionType,
  eventLabel: string,
  params: ConversionParams
) {
  try {
    const utms = getUtmParams();
    const pagePath = typeof window !== 'undefined' ? window.location.pathname : '';
    const pageTitle = typeof window !== 'undefined' ? document.title : '';

    await fetch('/api/track-conversion', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        event_label: eventLabel,
        page_path: pagePath,
        page_title: pageTitle,
        location: params.location || null,
        form_id: params.formId || null,
        dienst: params.dienst || null,
        vehicle_category: params.vehicleCategory || null,
        city_slug: params.citySlug || null,
        city_name: params.cityName || null,
        utm_source: utms.utm_source || null,
        utm_medium: utms.utm_medium || null,
        utm_campaign: utms.utm_campaign || null,
        utm_content: utms.utm_content || null,
        utm_term: utms.utm_term || null,
        session_id: getSessionId(),
        metadata: params.metadata || {},
      }),
    });
  } catch {
    // Silent fail — never block the user action
  }
}

// ─── Main tracking function ───────────────────────────────────────────────────

export function trackConversion(type: ConversionType, params: ConversionParams = {}) {
  const pagePath = typeof window !== 'undefined' ? window.location.pathname : '';
  const utms = getUtmParams();

  switch (type) {
    // ── Phone call ──────────────────────────────────────────────────────────
    case 'phone_call': {
      const label = `phone_${params.location || 'unknown'}`;

      fireGtag('phone_call', {
        event_category: 'conversion',
        event_label: label,
        cta_location: params.location || '',
        city_slug: params.citySlug || '',
        value: 1,
        currency: 'EUR',
      });

      fireGtag('generate_lead', {
        event_category: 'phone',
        event_label: label,
        lead_type: 'phone_call',
        location: params.location || '',
      });

      fireGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.phone_call, 1, 'EUR');

      saveConversionToSupabase('phone_call', label, params);
      sendHighValueAlert('phone_call', params, pagePath, utms);
      break;
    }

    // ── WhatsApp click ──────────────────────────────────────────────────────
    case 'whatsapp_click': {
      const label = `whatsapp_${params.location || 'unknown'}`;

      fireGtag('whatsapp_click', {
        event_category: 'conversion',
        event_label: label,
        cta_location: params.location || '',
        city_slug: params.citySlug || '',
        value: 1,
        currency: 'EUR',
      });

      fireGtag('generate_lead', {
        event_category: 'whatsapp',
        event_label: label,
        lead_type: 'whatsapp_click',
        location: params.location || '',
      });

      fireGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.whatsapp_click, 1, 'EUR');

      saveConversionToSupabase('whatsapp_click', label, params);
      sendHighValueAlert('whatsapp_click', params, pagePath, utms);
      break;
    }

    // ── Contact form submission ─────────────────────────────────────────────
    case 'contact_form': {
      const label = `contact_form_${params.location || 'contact'}`;

      fireGtag('contact_form_submit', {
        event_category: 'conversion',
        event_label: label,
        form_id: params.formId || 'contact',
        location: params.location || '',
        value: 5,
        currency: 'EUR',
      });

      fireGtag('generate_lead', {
        event_category: 'form',
        event_label: label,
        form_id: params.formId || 'contact',
        lead_type: 'contact_form',
      });

      fireGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.contact_form, 5, 'EUR');

      saveConversionToSupabase('contact_form', label, params);
      break;
    }

    // ── Offerte form submission ─────────────────────────────────────────────
    case 'offerte_form': {
      const label = `offerte_form_${params.dienst || 'onbekend'}`;

      fireGtag('offerte_form_submit', {
        event_category: 'conversion',
        event_label: label,
        form_id: params.formId || 'offerte',
        dienst: params.dienst || '',
        vehicle_category: params.vehicleCategory || '',
        value: 10,
        currency: 'EUR',
      });

      fireGtag('generate_lead', {
        event_category: 'form',
        event_label: label,
        form_id: 'offerte',
        lead_type: 'offerte_form',
        dienst: params.dienst || '',
      });

      fireGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.offerte_form, 10, 'EUR');

      saveConversionToSupabase('offerte_form', label, params);
      sendHighValueAlert('offerte_form', params, pagePath, utms);
      break;
    }

    // ── Homepage contact form ───────────────────────────────────────────────
    case 'homepage_contact_form': {
      const label = `homepage_contact_form_${params.dienst || 'onbekend'}`;

      fireGtag('homepage_contact_form_submit', {
        event_category: 'conversion',
        event_label: label,
        form_id: 'homepage-contact',
        dienst: params.dienst || '',
        vehicle_category: params.vehicleCategory || '',
        value: 10,
        currency: 'EUR',
      });

      fireGtag('generate_lead', {
        event_category: 'form',
        event_label: label,
        form_id: 'homepage-contact',
        lead_type: 'homepage_contact_form',
      });

      fireGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.offerte_form, 10, 'EUR');

      saveConversionToSupabase('homepage_contact_form', label, params);
      sendHighValueAlert('homepage_contact_form', params, pagePath, utms);
      break;
    }

    // ── Appointment / Maak een afspraak click ──────────────────────────────
    case 'appointment_click': {
      const label = `appointment_click_${params.location || 'unknown'}`;

      fireGtag('appointment_click', {
        event_category: 'conversion',
        event_label: label,
        cta_location: params.location || '',
        value: 5,
        currency: 'EUR',
      });

      fireGtag('generate_lead', {
        event_category: 'appointment',
        event_label: label,
        lead_type: 'appointment_click',
        location: params.location || '',
      });

      fireGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.appointment_click, 5, 'EUR');

      saveConversionToSupabase('appointment_click', label, params);
      break;
    }

    // ── Offerte button click (CTA, not form submit) ─────────────────────────
    case 'offerte_button_click': {
      const label = `offerte_button_${params.location || 'unknown'}`;

      fireGtag('offerte_button_click', {
        event_category: 'conversion',
        event_label: label,
        cta_location: params.location || '',
        value: 3,
        currency: 'EUR',
      });

      saveConversionToSupabase('offerte_button_click', label, params);
      break;
    }
  }
}

// ─── Convenience wrappers (drop-in replacements) ──────────────────────────────

export function trackPhoneCall(location: string, extra?: ConversionParams) {
  trackConversion('phone_call', { location, ...extra });
}

export function trackWhatsAppClick(location: string, extra?: ConversionParams) {
  trackConversion('whatsapp_click', { location, ...extra });
}

export function trackContactFormSubmit(location: string, extra?: ConversionParams) {
  trackConversion('contact_form', { location, formId: 'contact', ...extra });
}

export function trackOfferteFormSubmit(dienst: string, vehicleCategory?: string, extra?: ConversionParams) {
  trackConversion('offerte_form', { dienst, vehicleCategory, formId: 'offerte', ...extra });
}

export function trackHomepageContactFormSubmit(dienst: string, vehicleCategory?: string) {
  trackConversion('homepage_contact_form', { dienst, vehicleCategory, formId: 'homepage-contact' });
}

export function trackAppointmentClick(location: string, extra?: ConversionParams) {
  trackConversion('appointment_click', { location, ...extra });
}

export function trackOfferteButtonClick(location: string, extra?: ConversionParams) {
  trackConversion('offerte_button_click', { location, ...extra });
}
